import { NavigateFunction } from 'react-router-dom';

import { putModelFeedbackData } from 'common/api';
import { IWorkFlowFeedback } from 'common/api/type-def';
import { ReactComponent as ModalConObj } from 'common/assets/images/control-objective.svg';
import { ReactComponent as ModalConQualGramCheck } from 'common/assets/images/control-quality-grammar-check.svg';
import { ReactComponent as ModalConStat } from 'common/assets/images/control-statement.svg';
import { ReactComponent as ModalConTestScr } from 'common/assets/images/control-test-scripts.svg';
import { ReactComponent as RecipeObg } from 'common/assets/images/obligation.svg';
import { ReactComponent as ModalPlainEngSum } from 'common/assets/images/plain-english-summary.svg';
import { ReactComponent as ModalRegSum } from 'common/assets/images/regulation-summary.svg';
import { ReactComponent as RecipeReg } from 'common/assets/images/regulation.svg';
import { ReactComponent as ModalRiskStat } from 'common/assets/images/risk-statement.svg';
import { FEEDBACK_OBJECT_TYPE } from 'common/constants';
import {
	AI_SIMULATION_TYPES,
	AiRecipePageTitle,
} from 'common/constants/AISimulationConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { urlWithTenant } from 'common/utils/utilFunctions';

import { TYPE_OPTIONS } from 'features/Settings/PromptBuilder/PromptBuilderDetails/PromptBuilderConstants';

import { AICardsValue, IAIRecipeItem } from './api/type-def';
import styles from './SimulationArena/SimulationArena.module.scss';

const {
	PLAIN_ENGLISH_SUMMARY,
	REGULATION_SUMMARY,
	OBLIGATION_TO_POLICY,
	RISK_STATEMENT,
	CONTROL_OBJECTIVE,
	CONTROL_STATEMENT,
	CONTROL_TEST_SCRIPTS,
	CONTROL_QUALITY_GRAMMAR_CHECK,
} = AI_SIMULATION_TYPES;

const {
	riskStatement,
	controlObjective,
	controlStatement,
	controlTestScripts,
	controlQualityGrammerCheck,
	controlQuality,
	plainEnglishSummary,
	regulationSummary,
	obligationToPolicy,
	obligationAndControl,
	generate,
	simulationArena,
} = rrcpDashboardRoutes;

export const getAiSimulationTitle = (simulationType: string) => {
	switch (simulationType) {
		case plainEnglishSummary:
			return String(PLAIN_ENGLISH_SUMMARY);
		case regulationSummary:
			return String(REGULATION_SUMMARY);
		case obligationToPolicy:
			return String(OBLIGATION_TO_POLICY);
		default:
			return AiRecipePageTitle;
	}
};

export const navigateToRegulationControl = (
	navigate: NavigateFunction,
	regulationText: string,
) => {
	if (regulationText) {
		navigate(-1);
	} else {
		navigate(
			urlWithTenant(`/${simulationArena}/${obligationAndControl}/${generate}`),
		);
	}
};

const { imageColor } = styles;

export const aiRecipes: AICardsValue[] = [
	{
		id: '1',
		label: 'Regulation obligations and control generation',
		path: `${obligationAndControl}/${generate}`,
		icon: <RecipeReg fill={imageColor} />,
		desc: 'End-to-End flow for generating control measures based on regulatory obligations',
	},
	{
		id: '2',
		label: 'Obligation to policy',
		path: `${obligationToPolicy}/${generate}`,
		icon: <RecipeObg fill={imageColor} />,
		desc: 'End to End flow to search for policies that may have been impacted by obligations.',
	},
];

export const nestedAIModels: any = {
	create: [
		{
			id: '1',
			label: RISK_STATEMENT,
			path: `${riskStatement}`,
			icon: <ModalRiskStat />,
			desc: 'Generates precise and accurate Risk Statements from Plain English summary',
		},
		{
			id: '2',
			label: CONTROL_OBJECTIVE,
			path: `${controlObjective}`,
			icon: <ModalConObj />,
			desc: 'Generates Control Objective from Plain English summary',
		},
		{
			id: '3',
			label: CONTROL_STATEMENT,
			path: `${controlStatement}`,
			icon: <ModalConStat />,
			desc: 'Generates Control Statements from Plain English summary',
		},
		{
			id: '4',
			label: CONTROL_TEST_SCRIPTS,
			path: `${controlTestScripts}`,
			icon: <ModalConTestScr />,
			desc: 'Generates Control Test Scripts from Control Statements',
		},
	],
	improve: [
		{
			id: '1',
			label: CONTROL_QUALITY_GRAMMAR_CHECK,
			path: `${controlQualityGrammerCheck}`,
			icon: <ModalConQualGramCheck />,
			desc: 'Ensure the accuracy and correctness of control measures and assess the grammar and language usage.',
		},
	],
	summarize: [
		{
			id: '1',
			label: PLAIN_ENGLISH_SUMMARY,
			path: `${plainEnglishSummary}/${generate}`,
			icon: <ModalPlainEngSum />,
			desc: 'Transforms complex texts into simplified and comprehensible plain english summary.',
		},
		{
			id: '2',
			label: REGULATION_SUMMARY,
			path: `${regulationSummary}/${generate}`,
			icon: <ModalRegSum />,
			desc: 'Transforms complex regulatory citation texts into simplified and comprehensible plain english summary.',
		},
	],
	q_A: [
		{
			id: '1',
			label: 'Ask Risk Link',
			path: '',
			icon: <ModalRiskStat />,
			desc: 'Lorem ipsum dolor sit amet consectetur. Vel odio mi ipsum nunc leo tempus malesuada.',
		},
		{
			id: '2',
			label: 'Regulation Key Obligations Review',
			path: '',
			icon: <ModalConObj />,
			desc: 'Lorem ipsum dolor sit amet consectetur. Vel odio mi ipsum nunc leo tempus malesuada.',
		},
	],
};

export const getRoutePathForModel = (modelName: string, isShared = false) => {
	let path = '';
	switch (modelName) {
		case RISK_STATEMENT:
			path = `${riskStatement}`;
			break;
		case CONTROL_OBJECTIVE:
			path = `${controlObjective}`;
			break;
		case CONTROL_STATEMENT:
			path = `${controlStatement}`;
			break;
		case CONTROL_TEST_SCRIPTS:
			path = `${controlTestScripts}`;
			break;
		case CONTROL_QUALITY_GRAMMAR_CHECK:
			path = `${controlQualityGrammerCheck}/${controlQuality}`;
			break;
		case PLAIN_ENGLISH_SUMMARY:
			path = `${plainEnglishSummary}`;
			break;
		case REGULATION_SUMMARY:
			path = `${regulationSummary}`;
			break;
		default:
			path = `${plainEnglishSummary}`;
			break;
	}
	return isShared ? `/${simulationArena}/${path}` : path;
};

export const getAiRecipePath = (models: IAIRecipeItem[], isShared = false) => {
	if (models.some((model) => model.workflowStep === OBLIGATION_TO_POLICY)) {
		return isShared
			? `/${simulationArena}/${obligationToPolicy}`
			: obligationToPolicy;
	}
	return isShared
		? `/${simulationArena}/${obligationAndControl}`
		: obligationAndControl;
};

export const updateFeedbackOnSave = async (
	id: string,
	workflowFeedback: IWorkFlowFeedback[],
) => {
	const updateFeedback = workflowFeedback.filter((e) => e.regenerate);
	const putFeedBackAPI: any = [];
	updateFeedback.forEach((feedbackObj) => {
		const { newFeedback, existingFeedback } = feedbackObj;
		if (newFeedback) {
			const feedbackData = {
				...newFeedback,
				object_data: { ...newFeedback.objectData, reference: id },
			};
			putFeedBackAPI.push(putModelFeedbackData(feedbackData.id, feedbackData));
		}
		if (existingFeedback) {
			const feedbackData = {
				...existingFeedback,
				object_data: { ...existingFeedback.objectData, reference: '' },
			};
			putFeedBackAPI.push(putModelFeedbackData(feedbackData.id, feedbackData));
		}
	});
	const response = await Promise.all(putFeedBackAPI);
	return response;
};

const {
	REGULATORY_SUMMARIZATION,
	OBLIGATION_TO_POLICY_TYPE,
	PLAIN_ENGLISH_SUMMARY_TYPE,
	RISK_STATEMENT_TYPE,
	CONTROL_OBJECTIVE_TYPE,
	CONTROL_STATEMENT_TYPE,
	CONTROL_TEST_SCRIPTS_TYPE,
	CONTROL_QUALITY_GRAMMAR_CHECK_TYPE,
} = FEEDBACK_OBJECT_TYPE;

const { COMPLETION, CHAT_COMPLETION } = TYPE_OPTIONS;

export const typeSelection = [
	{ value: CHAT_COMPLETION, label: CHAT_COMPLETION },
	{ value: COMPLETION, label: COMPLETION },
];

export const mapTitleWithModel: any = {
	[REGULATION_SUMMARY]: REGULATORY_SUMMARIZATION,
	[OBLIGATION_TO_POLICY]: OBLIGATION_TO_POLICY_TYPE,
	[PLAIN_ENGLISH_SUMMARY]: PLAIN_ENGLISH_SUMMARY_TYPE,
	[RISK_STATEMENT]: RISK_STATEMENT_TYPE,
	[CONTROL_TEST_SCRIPTS]: CONTROL_TEST_SCRIPTS_TYPE,
	[CONTROL_STATEMENT]: CONTROL_STATEMENT_TYPE,
	[CONTROL_OBJECTIVE]: CONTROL_OBJECTIVE_TYPE,
	[CONTROL_QUALITY_GRAMMAR_CHECK]: CONTROL_QUALITY_GRAMMAR_CHECK_TYPE,
};
